'use client'

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Popover, PopoverButton, PopoverGroup, PopoverPanel, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { AlignJustify, BadgePercent, BookOpenText, Check, ChevronDown, ChevronRight, ChevronRightIcon, Eye, Flame, Gift, Heart, LayoutGrid, ListX, Medal, MoveRight, Package, PackageX, Percent, Plus, Search, ShieldAlert, ShoppingBag, ShoppingCart, Sparkles, Star, Tag, Trash2, Truck, User as UserIcon, Wallet } from 'lucide-react'
import { classNames, formatImageURL, formatPrice, getBackgroundColorStyles, getCartTotal, getFontClass, getTextColorStyles, getVendorPrimaryColor, getProductDisplayPrice, isCustomerAuthActive, resolveColorHex, getBuyXTriggerCount, getBuyXGetYRewardRule } from '@/utils/util'
import { useSession } from 'next-auth/react'

import Image from 'next/image'
import CartEmpty from '@/assets/cart-empty.png'

import { Button } from '@/components/elements/Button'
import { usePathname, useSearchParams } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import Skeleton from '@/components/elements/Skeleton'
import EmptyState from '@/components/elements/EmptyState'
import { useCart, useSearch, useNavigation } from '@/lib/commerce/hooks'
import Avatar from 'boring-avatars'
import SubHeader from './SubHeader'
import CartItemSidebar from '../cart/CartItemSidebar'
import { Progress } from '@/components/ui/progress'

export default function Header({ vendor, categories, subscription, campaigns, mobileMenuOpen, setMobileMenuOpen }: { vendor?: any, categories?: any, subscription?: any, campaigns?: any, mobileMenuOpen: boolean, setMobileMenuOpen: (open: boolean) => void }) {
  const { data: session } = useSession()

  const { initialLoading, cartCount, cart, addOne, removeOne, removeFromCart, validateCart, isValidating, canCheckout, clearCart, buyXGetYInfo, addRewardItem, availableOrderRewardItems, rewardLoading, freeShippingProgress, amount, sale, activeOrderDiscount, orderDiscountAmount, appliedCoupon, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, loyaltyDiscountAmount } = useCart()
  const { query, setQuery, results: filteredProducts, categories: searchCategories, loading: isSearching, history: searchHistory, clearHistory: clearSearchHistory, runSearch } = useSearch()
  const { navigation, isLoadingCategories, activeParentCategory, setActiveParentCategory, hoveredParentCategory, setHoveredParentCategory } = useNavigation({
    vendor,
    subscription,
    campaigns,
    categories,
    icons: { catalog: AlignJustify, campaign: Gift, featured: Star, new: Sparkles, sale: Flame, brand: Tag, blog: BookOpenText, gift: Gift, loyalty: Medal },
  })
  const catalogBtnRef = useRef<HTMLButtonElement>(null)
  const catalogCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelCatalogClose = () => {
    if (catalogCloseTimer.current) {
      clearTimeout(catalogCloseTimer.current)
      catalogCloseTimer.current = null
    }
  }
  const cartItems = cart?.products || []
  const cartTotal = useMemo(() => {
    const total = getCartTotal(cartItems, !!session?.user?.id)
    const remaining = Math.max(0, total - (orderDiscountAmount || 0))
    const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, remaining) : 0
    return Math.max(0, total - (orderDiscountAmount || 0) - loyaltyDiscount)
  }, [cartItems, session?.user?.id, loyaltyDiscountAmount, orderDiscountAmount])
  const pathname = usePathname()

  const [searchFocused, setSearchFocused] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [openCart, setOpenCart] = useState(false)

  const searchParams = useSearchParams()

  const handleCartOpen = async () => {
    setOpenCart(true)
    if (cartCount > 0 && !isValidating) {
      try {
        await validateCart()
      } catch (error) {
        console.error('Error validating cart:', error)
      }
    }
  }

  const handleCartClose = () => {
    setOpenCart(false)
  }

  useEffect(() => {
    if (searchParams) {
      const search = new URLSearchParams(searchParams.toString()).get('search')
      if (search) {
        setQuery(search)
      }
    }
  }, [searchParams])

  // Header bar is position:fixed so macOS rubber-band overscroll can't drag it (sticky would ride the
  // bounce). We shift it up by the scroll amount (clamped >=0, capped at the SubHeader height) so only
  // the SubHeader scrolls away and the nav stays pinned. The fixed bar reserves no space, so an
  // invisible normal-flow GHOST of the bar (below) holds its EXACT height on the SSR first paint — no
  // measurement, no hydration flash.
  const subHeaderRef = useRef<HTMLDivElement>(null)
  const subHeaderHeightRef = useRef(0)
  const [barShift, setBarShift] = useState(0)
  useEffect(() => {
    const el = subHeaderRef.current
    if (!el) return
    const update = () => { subHeaderHeightRef.current = el.offsetHeight }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const y = Math.max(0, window.scrollY) // clamp overscroll -> bar never rides the bounce
        setBarShift(Math.min(y, subHeaderHeightRef.current))
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  const fontClass = getFontClass(vendor?.font)
  const primaryColor = getVendorPrimaryColor(vendor)
  const primaryHex = resolveColorHex(primaryColor)

  const { style: backgroundColorStyle } = getBackgroundColorStyles(primaryColor)
  const { className: textColorClass, style: textColorStyle } = getTextColorStyles(primaryColor)

  const displayedCategory = hoveredParentCategory || activeParentCategory

  const searchBar = (
    <div className="flex-1 relative">
      <div
        className="flex items-center h-11 bg-muted rounded-lg border-2 transition-colors"
        style={{ borderColor: searchFocused ? primaryHex : 'transparent' }}
      >
        <Search className="h-4 w-4 mx-3.5 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
          onKeyDown={e => { if (e.key === 'Enter') runSearch() }}
          placeholder="Бүтээгдэхүүн хайх.."
          className="flex-1 h-full bg-transparent border-0 outline-none text-sm pr-3 focus:outline-none focus:ring-0 focus:border-0 placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={() => runSearch()}
          className="h-9 mr-1 px-4 rounded-md text-white text-sm font-medium inline-flex items-center gap-1.5 hover:opacity-95 transition-opacity cursor-pointer"
          style={{ background: primaryHex }}
        >
          <Search className="h-4 w-4" />
          Хайх
        </button>
      </div>

      {searchFocused && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-border rounded-xl shadow-lg py-3 z-20 max-h-[480px] overflow-y-auto">
          {query.trim() ? (
            isSearching ? (
              <div className="px-4 py-2 space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-md" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (filteredProducts.length > 0 || searchCategories.length > 0) ? (
              <>
                {searchCategories.length > 0 && (
                  <>
                    <div className="px-4 pb-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Ангилал</div>
                    <div className="px-2 pb-2 flex flex-wrap gap-1.5">
                      {searchCategories.map((cat: any) => (
                        <a
                          key={cat.id}
                          href={navigateUrl(`/product?category=${cat.slug || cat.id}`, pathname)}
                          onMouseDown={e => e.preventDefault()}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-muted hover:bg-muted/70 text-sm transition-colors"
                        >
                          <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="truncate max-w-40">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </>
                )}
                {filteredProducts.length > 0 && (
                  <div className="px-4 pb-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Илэрц</div>
                )}
                {filteredProducts.slice(0, 6).map((product: any) => {
                  const variant = product?.variant
                  const imageUrl = variant?.images?.length > 0 ? formatImageURL(variant.images[0]) : formatImageURL('placeholder.png')
                  const price = getProductDisplayPrice(product, variant, !!session?.user?.id)
                  return (
                    <a
                      key={product.id}
                      href={navigateUrl(`/product/${product.customId || product.id}`, pathname)}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-muted text-left"
                      onMouseDown={e => e.preventDefault()}
                    >
                      <img src={imageUrl} alt={product?.name} className="h-10 w-10 rounded-md object-cover bg-zinc-100" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{product?.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{variant?.name || ''}</div>
                      </div>
                      <div className="text-sm font-semibold text-foreground tabular-nums shrink-0">{formatPrice(price)}</div>
                    </a>
                  )
                })}
                {filteredProducts.length > 0 && (
                  <button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => runSearch()}
                    className="w-full mt-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted text-center transition-colors"
                  >
                    "{query}" гэсэн бүх илэрцийг үзэх
                  </button>
                )}
              </>
            ) : (
              <div className="px-4 py-3 text-sm text-muted-foreground">Илэрц олдсонгүй</div>
            )
          ) : (
            searchHistory.length > 0 ? (
              <>
                <div className="px-4 pb-2 flex items-center justify-between">
                  <div className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">Сүүлд хайсан</div>
                  <button type="button" onMouseDown={e => e.preventDefault()} onClick={clearSearchHistory} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                    Цэвэрлэх
                  </button>
                </div>
                {searchHistory.map(term => (
                  <button
                    key={term}
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => runSearch(term)}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted text-left"
                  >
                    <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm flex-1 truncate">{term}</span>
                  </button>
                ))}
              </>
            ) : (
              <div className="px-4 py-3 text-sm text-muted-foreground">Хайлтын түүх байхгүй байна</div>
            )
          )}
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-40 will-change-transform" style={{ transform: `translateY(-${barShift}px)` }}>
        <div ref={subHeaderRef}>
          {subscription?.canUseHeadBanner && vendor?.headBanner && (
            <SubHeader fontClass={fontClass} vendor={vendor} />
          )}
        </div>
        <header className={`w-full isolate backdrop-blur-xl bg-background/75 ${fontClass} border-b border-border`}>
        {/* Mobile row */}
        <nav className="lg:hidden" aria-label="Global">
          <div className="cont flex items-center justify-between gap-2 py-2">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="h-10 w-10 grid place-items-center rounded-lg hover:bg-muted text-foreground transition-colors cursor-pointer"
              aria-label="Menu"
            >
              <AlignJustify className="h-5 w-5" strokeWidth={2} />
            </button>
            <a href={navigateUrl(`/`, pathname)} className="flex items-center gap-2 min-w-0">
              {vendor?.logo ? (
                <img src={formatImageURL(vendor?.logo)} alt="logo" className="max-h-7 max-w-[170px] w-auto shrink-0" />
              ) : (
                <div className={`text-lg font-bold tracking-tight truncate ${textColorClass}`} style={textColorStyle}>
                  {vendor?.name}
                </div>
              )}
            </a>
            <button
              type="button"
              onClick={() => setMobileSearchOpen(o => !o)}
              className="h-10 w-10 grid place-items-center rounded-lg hover:bg-muted text-foreground transition-colors cursor-pointer"
              aria-label="Search"
            >
              {mobileSearchOpen ? <XMarkIcon className="h-5 w-5" /> : <Search className="h-5 w-5" strokeWidth={2} />}
            </button>
          </div>
          {mobileSearchOpen && (
            <div className="cont pb-3">
              {searchBar}
            </div>
          )}
        </nav>

        {/* Desktop row 1: logo + search + actions */}
        <div className="hidden lg:block mx-auto w-full max-w-[1280px] px-4 lg:px-8">
          <div className="h-[76px] flex items-center gap-6">
            <a href={navigateUrl(`/`, pathname)} className="flex items-center shrink-0">
              {vendor?.logo ? (
                <img src={formatImageURL(vendor?.logo)} alt="logo" className="h-9 max-w-[190px] w-auto" />
              ) : (
                <div className={`text-2xl font-bold tracking-tight ${textColorClass} hover:opacity-80 transition-opacity`} style={textColorStyle}>
                  {vendor?.name}
                </div>
              )}
            </a>

            {searchBar}

            <div className="flex items-center gap-1 shrink-0">
              {isCustomerAuthActive(vendor) && (
                <a
                  href={navigateUrl(session?.user?.id ? `/wishlist` : `/login?url=/wishlist`, pathname)}
                  className="h-11 w-11 grid place-items-center rounded-lg hover:bg-muted relative transition-colors"
                  aria-label="Wishlist"
                >
                  <Heart className="h-5 w-5" />
                </a>
              )}

              {vendor?.canPlaceOrder !== false && (
                <button
                  onClick={handleCartOpen}
                  className="h-11 pl-2 pr-3 rounded-lg hover:bg-muted flex items-center gap-2.5 transition-colors"
                  aria-label="Cart"
                >
                  <span className="relative">
                    <ShoppingCart className="h-5 w-5" />
                    {!initialLoading && cartCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold text-white grid place-items-center"
                        style={{ background: primaryHex }}
                      >
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </span>
                  <span className="hidden md:flex flex-col items-start leading-tight">
                    <span className="text-[10px] text-muted-foreground">Сагс</span>
                    <span className="text-sm font-semibold tracking-[.025em]">{formatPrice(cartTotal)}</span>
                  </span>
                </button>
              )}

              {isCustomerAuthActive(vendor) && (
                <>
                  <div className="w-px h-6 mx-1 bg-border" />
                  {session?.user?.id ? (
                    <a
                      href={navigateUrl(`/profile`, pathname)}
                      className="h-11 pl-1.5 pr-3 rounded-lg hover:bg-muted flex items-center gap-2 transition-colors"
                    >
                      {session.user?.avatar ? (
                        <img src={formatImageURL(session.user.avatar)} alt="profile" className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <Avatar size={32} variant="marble" name={session.user?.email || undefined} />
                      )}
                      <span className="hidden md:flex flex-col items-start leading-tight">
                        <span className="text-[10px] text-muted-foreground">Сайн уу</span>
                        <span className="text-sm font-semibold">{session.user.firstName || 'Хэрэглэгч'}</span>
                      </span>
                    </a>
                  ) : (
                    <a
                      href={navigateUrl(`/login`, pathname)}
                      className="h-11 px-4 rounded-lg text-sm font-medium hover:bg-muted transition-colors inline-flex items-center gap-2"
                    >
                      <UserIcon className="h-4 w-4" />
                      Нэвтрэх
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <nav className="hidden lg:block mx-auto w-full max-w-[1280px] px-4 lg:px-8" aria-label="Top">
          <PopoverGroup className="h-12 flex items-center gap-1 border-t border-border z-40 font-medium min-w-0">
            <Popover className="relative flex shrink-0">
              {({ open, close }) => (
                <>
                  <div
                    className="relative flex"
                    onMouseEnter={() => {
                      cancelCatalogClose()
                      if (!open) catalogBtnRef.current?.click()
                    }}
                    onMouseLeave={() => {
                      cancelCatalogClose()
                      catalogCloseTimer.current = setTimeout(() => close(), 150)
                    }}
                  >
                    <PopoverButton
                      ref={catalogBtnRef}
                      className="h-9 px-3 -ml-3 rounded-md text-sm font-medium flex items-center gap-2 text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus:ring-0"
                    >
                      <LayoutGrid className="h-4 w-4" />
                      {navigation.catalog.name}
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
                    </PopoverButton>
                  </div>
                  <Transition as={Fragment} enter="transition ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="transition ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0" >
                    <PopoverPanel
                      onMouseEnter={cancelCatalogClose}
                      onMouseLeave={() => {
                        cancelCatalogClose()
                        catalogCloseTimer.current = setTimeout(() => close(), 150)
                      }}
                      className="absolute left-0 top-full mt-2 w-[960px] max-w-[90vw] bg-white border border-border rounded-2xl shadow-xl overflow-hidden z-20 max-h-150"
                    >
                      {navigation.catalog.sections.length > 0 ? (
                        <div className="flex min-h-[400px] max-h-150">
                          <div className="w-56 bg-muted py-3 shrink-0 overflow-y-auto">
                            {isLoadingCategories ? (
                              <div className="px-3 space-y-2">
                                {[...Array(6)].map((_, i) => (
                                  <Skeleton key={i} className="h-9 w-full rounded" />
                                ))}
                              </div>
                            ) : (
                              <>
                                {navigation.catalog.sections.map((section: any) => {
                                  const active = activeParentCategory?.id === section.id || hoveredParentCategory?.id === section.id
                                  const hasChildren = section.items && section.items.length > 0
                                  return (
                                    <button
                                      key={section.id}
                                      onMouseEnter={() => setHoveredParentCategory(section)}
                                      onClick={() => {
                                        if (!hasChildren) {
                                          window.location.href = section.href
                                        } else {
                                          setActiveParentCategory(section)
                                        }
                                      }}
                                      className={classNames(
                                        'w-full h-11 px-4 flex items-center gap-3 text-sm text-left transition-colors',
                                        active ? 'bg-white font-semibold text-foreground' : 'text-foreground/80 hover:bg-white/60'
                                      )}
                                    >
                                      <span className="flex-1 truncate">{section.name}</span>
                                      {hasChildren && <ChevronRightIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                                    </button>
                                  )
                                })}
                                <a
                                  href={navigateUrl('/product', pathname)}
                                  className="w-full h-11 px-4 flex items-center gap-3 text-sm text-muted-foreground hover:bg-white/60 transition-colors border-t border-border/60 mt-1"
                                >
                                  <span className="flex-1">Бүх бүтээгдэхүүн</span>
                                  <ChevronRightIcon className="h-3.5 w-3.5 shrink-0" />
                                </a>
                              </>
                            )}
                          </div>

                          <div className="flex-1 p-6 min-w-0 overflow-y-auto">
                            {displayedCategory ? (
                              <>
                                <div className="flex items-center justify-between mb-4">
                                  <h2 className="text-base font-semibold text-foreground">{displayedCategory.name}</h2>
                                  <a href={displayedCategory.href} className="group inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                                      Бүтээгдэхүүн үзэх
                                    <MoveRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                                  </a>
                                </div>

                                {displayedCategory.items && displayedCategory.items.length > 0 ? (
                                  <div className="grid grid-cols-3 gap-6">
                                    {displayedCategory.items.map((item: any) => (
                                      <div key={item.name}>
                                        <a href={item.href} className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase mb-2.5 block hover:text-foreground transition-colors">
                                          {item.name}
                                        </a>
                                        {item.children && item.children.length > 0 && (
                                          <ul className="space-y-1.5">
                                            {item.children.map((child: any) => (
                                              <li key={child.name}>
                                                <a href={child.href} className="text-sm text-foreground hover:underline">{child.name}</a>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-muted-foreground py-8 text-center">Энэ ангилалд дэд ангилал байхгүй байна</div>
                                )}
                              </>
                            ) : (
                              <div className="text-sm text-muted-foreground py-8 text-center">Ангилал сонгоно уу</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center px-6 pb-6">
                          <EmptyState icon={ListX} title="Ангилал байхгүй байна" description="Одоогоор ангилал нэмэгдээгүй байна. Хэсэг хугацааны дараа дахин шалгана уу." />
                          <a href={navigateUrl('/product', pathname)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-border bg-white text-[13px] font-medium hover:border-foreground/30 transition-colors">
                              Бүх бүтээгдэхүүн үзэх <MoveRight className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      )}
                    </PopoverPanel>
                  </Transition>
                </>
              )}
            </Popover>

            <div className="w-px h-5 mx-2 bg-border shrink-0" />

            <a
              href={navigateUrl('/product', pathname)}
              className="h-9 px-3 rounded-md hover:bg-muted flex items-center gap-1.5 transition-colors text-[13px] font-medium whitespace-nowrap text-muted-foreground shrink-0"
            >
              Бүх бараа
              {/* Бүгд */}
            </a>

            <div className="flex items-center gap-1 text-[13px] overflow-x-auto scrollbar-hide flex-1 min-w-0">
              {navigation.links.map((item: any) => (
                item.isCampaign ? (
                  <a
                    key={item.name}
                    href={navigateUrl(item.href, pathname)}
                    className="relative h-9 pl-2.5 flex items-center gap-1.5 text-[13px] font-semibold text-white whitespace-nowrap hover:opacity-95 transition-opacity ml-1"
                    style={{
                      background: primaryHex,
                      clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)',
                      paddingRight: 18,
                    }}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {item.name}
                    <span
                      className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white border-2"
                      style={{ borderColor: primaryHex }}
                    />
                  </a>
                ) : (
                  <a
                    key={item.name}
                    href={navigateUrl(item.href, pathname)}
                    className="h-9 px-3 rounded-md hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors font-medium whitespace-nowrap text-muted-foreground"
                  >
                    {item.name}
                    {item.isSale && (
                      <span className="px-1 py-0.5 bg-orange-600 text-white rounded-full grid place-items-center">
                        <Percent className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                    )}
                  </a>
                )
              ))}
            </div>

          </PopoverGroup>
        </nav>


        <Transition show={openCart} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={handleCartClose}>
            <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/25" />
            </TransitionChild>
            <div className="fixed inset-0 z-50 flex justify-end">
              <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
                <DialogPanel className="relative flex w-full max-w-lg flex-col overflow-hidden bg-white shadow-xl rounded-sm">
                  <div className="relative items-center px-6 pt-10">
                    <h2 className="text-2xl leading-8 tracking-[.0125em] font-medium text-foreground">Сагс</h2>
                    <button type="button" className="absolute top-3 right-4 rounded-md p-2 text-foreground/80 hover:text-foreground transition-colors cursor-pointer" onClick={() => setOpenCart(false)}>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="px-6 pb-2 mt-1.5 text-sm text-muted-foreground">
                    {initialLoading ? (
                      <div className="flex items-center gap-4 justify-between">
                        <Skeleton className="w-20 h-4" />
                        <Skeleton className="w-24 h-4" />
                      </div>
                    ) : cartCount > 0 ? (
                      <div className="flex items-center gap-4 justify-between">
                        <span className='font-normal text-base leading-6 text-muted-foreground'>{cartCount} бүтээгдэхүүн</span>
                        <span className="font-medium leading-7 text-lg tracking-[.0125em] text-foreground">
                          Нийт дүн: {formatPrice(cartTotal)}
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                    {cartCount > 0 && (
                      <div className='mt-1.5 flex justify-start'>
                        <Button onClick={clearCart} className='!px-0 !py-0 !h-8' variant='ghost' icon={Trash2} color={primaryColor}>
                          Сагс хоослох
                        </Button>
                      </div>
                    )}
                    {freeShippingProgress && !cartItems.every((p: any) => p.isDigital === true) && (
                      <div className="flex flex-col gap-2 p-3 mt-3 bg-green-50 rounded-lg">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-1.5 text-green-700">
                            <Truck className="w-4 h-4" />
                            <span className="font-medium">Үнэгүй хүргэлт</span>
                          </div>
                          {freeShippingProgress.reached ? (
                            <div className="flex items-center gap-1 text-green-700 text-xs">
                              <Check className="w-3.5 h-3.5" />
                              <span className="font-medium">Хангагдсан</span>
                            </div>
                          ) : (
                            <span className="text-green-700 text-xs">{formatPrice(freeShippingProgress.remaining)} дутуу</span>
                          )}
                        </div>
                        <Progress value={freeShippingProgress.percentage} className="h-2 bg-green-200 [&>div]:bg-green-600" />
                      </div>
                    )}
                    {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
                      const subPrice = amount - sale
                      const minAmt = activeOrderDiscount.minOrderAmount
                      const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
                      if (!target) return null
                      const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
                      if (!discountLabel) return null
                      const reached = subPrice >= minAmt
                      return (
                        <div className="flex flex-col gap-2.5 p-3 mt-3 bg-purple-50 rounded-lg">
                          <div className="flex items-start gap-1.5 text-purple-700 text-sm">
                            <BadgePercent className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="font-medium">{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Progress value={Math.min((subPrice / minAmt) * 100, 100)} className="h-2 flex-1 bg-purple-200 [&>div]:bg-purple-600" />
                            {reached ? (
                              <div className="flex items-center gap-1 text-purple-700 text-xs shrink-0">
                                <Check className="w-3.5 h-3.5" />
                                <span className="font-medium">-{formatPrice(orderDiscountAmount)}</span>
                              </div>
                            ) : (
                              <span className="text-purple-700 text-xs font-medium shrink-0">{formatPrice(minAmt - subPrice)} дутуу</span>
                            )}
                          </div>
                        </div>
                      )
                    })()}
                  </div>

                  <div className="px-6">
                    <div className="h-[1px] bg-muted-foreground/10" />
                  </div>

                  <div className="flex-1 px-6 py-6 overflow-y-auto">
                    {initialLoading ? (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-4">
                          <Skeleton className="h-16 w-16 rounded" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Skeleton className="h-16 w-16 rounded" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                      </div>
                    ) : cartCount > 0 && cartItems?.length > 0 ? (
                      <div className="space-y-5">
                        {cartItems.map((product: any) => (
                          <CartItemSidebar
                            key={`${product.isRewardItem ? 'reward-' : ''}${product.id}-${product.variant?.id}`}
                            product={product}
                            pathname={pathname}
                          />
                        ))}
                        {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
                          const firstState = getBuyXGetYRewardState(info, info.rewardProducts[0])
                          const totalRemaining = firstState.totalRemaining
                          if (totalRemaining <= 0) return null
                          return (
                            <div key={info.discount.id} className="rounded-xl bg-linear-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-2 border-orange-500/30 border-dashed p-3">
                              <div className="flex items-center gap-2">
                                <div className="px-1.5 py-1 bg-orange-600 text-white rounded-full shrink-0">
                                  <Gift className="w-4 h-4" strokeWidth={2.5} />
                                </div>
                                <div>
                                  <div className="font-semibold text-sm text-foreground leading-4">{info.discount?.name || 'Хөнгөлөлт'}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">{getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 mt-1.5">
                                <Check className="w-4 h-4 text-green-600" />
                                <span className="text-xs text-green-600 font-medium">Нөхцөл хангагдлаа ({info.triggerCount}/{getBuyXTriggerCount(info.discount)}){totalRemaining > 0 ? ` — ${totalRemaining} урамшуулалтай бараа авах боломжтой` : ''}</span>
                              </div>
                              <div className="space-y-1.5 mt-2">
                                {info.rewardProducts.map((reward: any) => {
                                  const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                                  if (isLockedByOther) return null
                                  return (
                                    <div key={reward.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                                      <div className="w-10 h-10 shrink-0">
                                        <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full rounded-md object-cover" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <div className="font-medium text-xs text-foreground leading-4 line-clamp-1">{reward.name}</div>
                                          {reward.valueType === 'free' ? (
                                            <span className="bg-red-500 text-white px-1 py-0.5 rounded text-[9px] font-semibold leading-3 shrink-0">Үнэгүй</span>
                                          ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                            <span className="bg-red-500 text-white px-1 py-0.5 rounded text-[9px] font-semibold leading-3 shrink-0">-{reward.valuePercent}%</span>
                                          ) : null}
                                        </div>
                                        <div className="flex items-center gap-1 mt-0.5">
                                          <span className={`text-xs font-medium ${rewardPrice === 0 ? 'text-green-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                          {reward.price && rewardPrice < reward.price && <span className="line-through text-muted-foreground text-xs">{formatPrice(reward.price)}</span>}
                                        </div>
                                      </div>
                                      <Button onClick={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')} size="sm" disabled={!canAdd || rewardLoading} isLoading={rewardLoading} loadingMessage="Нэмэх" color={primaryColor} icon={canAdd && !rewardLoading ? Plus : undefined}>
                                        {isOutOfStock ? 'Дууссан' : canAdd ? 'Нэмэх' : 'Нэмэгдсэн'}
                                      </Button>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })())}
                        {availableOrderRewardItems?.length > 0 && (
                          <div className="rounded-xl bg-linear-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border-2 border-orange-500/30 border-dashed p-3">
                            <div className="flex items-center gap-2">
                              <div className="px-1.5 py-1 bg-orange-600 text-white rounded-full shrink-0">
                                <Gift className="w-4 h-4" strokeWidth={2.5} />
                              </div>
                              <div>
                                <div className="font-semibold text-sm text-foreground leading-4">Захиалгын бэлэг</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Захиалгын хөнгөлөлтийн бэлэг</div>
                              </div>
                            </div>
                            <div className="space-y-1.5 mt-2">
                              {availableOrderRewardItems.map((reward: any) => {
                                const isOutOfStock = isRewardOutOfStock(reward)
                                const rewardPrice = getRewardPrice(reward)
                                return (
                                  <div key={reward.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                                    <div className="w-10 h-10 shrink-0">
                                      <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full rounded-md object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <div className="font-medium text-xs text-foreground leading-4 line-clamp-1">{reward.name}</div>
                                        {reward.valueType === 'free' ? (
                                          <span className="bg-red-500 text-white px-1 py-0.5 rounded text-[9px] font-semibold leading-3 shrink-0">Үнэгүй</span>
                                        ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                          <span className="bg-red-500 text-white px-1 py-0.5 rounded text-[9px] font-semibold leading-3 shrink-0">-{reward.valuePercent}%</span>
                                        ) : null}
                                      </div>
                                      <div className="flex items-center gap-1 mt-0.5">
                                        <span className={`text-xs font-medium ${rewardPrice === 0 ? 'text-green-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                        {reward.price && rewardPrice < reward.price && <span className="line-through text-muted-foreground text-xs">{formatPrice(reward.price)}</span>}
                                      </div>
                                    </div>
                                    <Button onClick={() => addRewardItem(reward, 'order')} size="sm" disabled={isOutOfStock || rewardLoading} isLoading={rewardLoading} loadingMessage="Нэмэх" color={primaryColor} icon={!isOutOfStock && !rewardLoading ? Plus : undefined}>{isOutOfStock ? 'Дууссан' : 'Нэмэх'}</Button>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Image src={CartEmpty} alt="Cart Empty" className="w-40 h-40 mb-2" />
                        <p className="text-muted-foreground text-center">Таны сагс хоосон байна</p>
                        <Button onClick={() => { setOpenCart(false); window.location.href = navigateUrl('/product', pathname) }} className="mt-4" color={primaryColor} icon={ShoppingBag}>
                          Дэлгүүр хэсэх
                        </Button>
                      </div>
                    )}
                  </div>

                  {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
                    <div className="px-6">
                      <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                        <div className="flex items-start gap-3">
                          <PackageX className='w-4 h-4 text-red-500 shrink-0 mt-1' />
                          <div className="flex flex-col gap-1">
                            <span className="text-sm text-red-500 font-medium leading-6">Үлдэгдэл дууссан бараа</span>
                            <span className='text-xs text-red-500'>Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {cartCount > 0 && (
                    <div className="px-6 py-5">
                      <div className="space-y-2">
                        {vendor?.orderMinAmount > 0 && (amount - sale) < vendor.orderMinAmount && (
                          <div className="flex flex-col gap-2 p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1.5 text-blue-700">
                                <ShoppingBag className="w-4 h-4" />
                                <span className="font-medium">Захиалгын доод дүн</span>
                              </div>
                              <span className="text-blue-700 text-xs">{formatPrice(vendor.orderMinAmount - (amount - sale))} дутуу</span>
                            </div>
                            <Progress value={((amount - sale) / vendor.orderMinAmount) * 100} className="h-2 bg-blue-200 [&>div]:bg-blue-600" />
                          </div>
                        )}
                        <Button onClick={() => { setOpenCart(false); window.location.href = navigateUrl('/cart', pathname) }} className="w-full" variant="outline" icon={ShoppingCart} border={vendor?.border}>
                          Сагс үзэх
                        </Button>
                        <Button
                          onClick={() => {
                            setOpenCart(false);
                            if (session?.user?.id || vendor?.canGuestOrder === true || !isCustomerAuthActive(vendor)) {
                              window.location.href = navigateUrl('/checkout', pathname)
                            } else {
                              window.location.href = navigateUrl('/checkout/login', pathname)
                            }
                          }}
                          className="w-full"
                          border={vendor?.border}
                          icon={Wallet}
                          color={primaryColor}
                          disabled={!canCheckout()}
                        >
                          Худалдан авах
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>

        <Transition show={mobileMenuOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileMenuOpen}>
            <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/25" />
            </TransitionChild>
            <div className="fixed inset-0 z-40 flex">
              <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
                <DialogPanel className="relative flex w-[82%] max-w-xs flex-col overflow-hidden bg-white shadow-2xl rounded-r-2xl">
                  <div className="sticky top-0 bg-white/95 backdrop-blur z-10 flex justify-between items-center px-4 h-14 border-b border-border">
                    <a href={navigateUrl(`/`, pathname)} className="flex items-center gap-2">
                      {vendor?.logo ? (
                        <img src={formatImageURL(vendor?.logo)} alt="logo" className="max-h-[30px] max-w-[170px] w-auto" />
                      ) : (
                        <div className={`text-lg font-bold tracking-tight ${textColorClass}`} style={textColorStyle}>
                          {vendor?.name}
                        </div>
                      )}
                    </a>
                    <button type="button" className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto overscroll-contain">
                    <div className="px-3 py-3">
                      <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase px-3 mb-1.5">Ангилал</div>
                      <a href={navigateUrl('/product', pathname)} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${pathname.includes('/product') && !pathname.includes('/product/') ? `bg-muted ${textColorClass}` : 'text-foreground hover:bg-muted'}`} style={pathname.includes('/product') && !pathname.includes('/product/') ? textColorStyle : undefined} onClick={() => setMobileMenuOpen(false)}>
                        <Package className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                        <span className="flex-1">Бүх бараа</span>
                      </a>
                      <Disclosure as="div">
                        {({ open }) => (
                          <>
                            <DisclosureButton className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors w-full">
                              <LayoutGrid className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                              <span className="flex-1 text-left">{navigation.catalog.name}</span>
                              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform text-muted-foreground ${open ? 'rotate-180' : ''}`} />
                            </DisclosureButton>
                            <DisclosurePanel as="div" className="pl-4 mt-0.5">
                              {navigation.catalog.sections.map((section: any, sectionIdx: any) => (
                                <Disclosure as="div" key={sectionIdx}>
                                  {({ open: sectionOpen }) => (
                                    <>
                                      {(!section.items || section.items.length === 0) ? (
                                        <a href={section.href} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${pathname === section.href ? `bg-muted font-medium ${textColorClass}` : 'text-foreground hover:bg-muted'}`} style={pathname === section.href ? textColorStyle : undefined} onClick={() => setMobileMenuOpen(false)}>
                                          <span>{section.name}</span>
                                        </a>
                                      ) : (
                                        <>
                                          <div className="flex items-center">
                                            <DisclosureButton className="flex-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
                                              <span className="flex-1 text-left">{section.name}</span>
                                              <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform text-muted-foreground ${sectionOpen ? 'rotate-180' : ''}`} />
                                            </DisclosureButton>
                                            <a href={section.href} className="ml-1 h-8 w-8 grid place-items-center hover:bg-muted rounded-lg transition-colors" onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false) }} title="Бүх барааг үзэх">
                                              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                                            </a>
                                          </div>
                                          <DisclosurePanel as="div" className="ml-3 border-l border-border pl-2 mt-0.5">
                                            {section.items.map((item: any) => (
                                              <a key={item.name} href={item.href} className={`flex items-center rounded-lg px-3 py-1.5 text-sm transition-colors ${pathname === item.href ? `bg-muted font-medium ${textColorClass}` : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} style={pathname === item.href ? textColorStyle : undefined} onClick={() => setMobileMenuOpen(false)}>
                                                <span>{item.name}</span>
                                              </a>
                                            ))}
                                          </DisclosurePanel>
                                        </>
                                      )}
                                    </>
                                  )}
                                </Disclosure>
                              ))}
                            </DisclosurePanel>
                          </>
                        )}
                      </Disclosure>
                    </div>

                    <div className="px-3 pb-3">
                      <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase px-3 mb-1.5">Бусад</div>
                      <div className="flex flex-col gap-1">
                        {navigation.links.map((page: any) => (
                          page.isCampaign ? (
                            <a
                              key={page.name}
                              href={navigateUrl(page.href, pathname)}
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-white hover:opacity-95 transition-opacity"
                              style={{ background: primaryHex }}
                            >
                              <Sparkles className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                              <span className="flex-1">{page.name}</span>
                            </a>
                          ) : (
                            <a key={page.name} href={navigateUrl(page.href, pathname)} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${pathname.includes(page.href) ? `bg-muted ${textColorClass}` : 'text-foreground hover:bg-muted'}`} style={pathname.includes(page.href) ? textColorStyle : undefined} onClick={() => setMobileMenuOpen(false)}>
                              <page.icon className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                              <span className="flex-1">{page.name}</span>
                              {page.isSale && (
                                <span className="px-1 py-0.5 bg-orange-600 text-white rounded-full grid place-items-center">
                                  <Percent className="w-3 h-3" strokeWidth={2.5} />
                                </span>
                              )}
                            </a>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>

        {vendor?.isDanVerified === false && (
          <div className="bg-amber-50 border-t border-amber-200">
            <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 h-9 flex items-center justify-between text-[12.5px]">
              <div className="flex items-center gap-2 text-amber-900 min-w-0">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
                <span className="truncate">
                  <span className="font-semibold">Энэ дэлгүүр баталгаажаагүй байна.</span>
                  <span className="hidden sm:inline opacity-80"> Худалдан авалт хийхдээ анхаарна уу.</span>
                </span>
              </div>
              <a href={navigateUrl('/status', pathname)} className="inline-flex items-center gap-1 text-amber-900 font-semibold hover:underline shrink-0">
                Дэлгэрэнгүй
                <ChevronRight className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}
        </header>
      </div>
      {/* Ghost: invisible normal-flow mirror of the fixed bar — reserves its EXACT height on the SSR
          first paint so content never sits behind the bar (no JS measurement, no hydration flash).
          Mirrors each row with its own classes; keep heights/breakpoints/conditions in sync above.
          (Mobile search row is omitted — it's behind mobileSearchOpen state, off at load.) */}
      <div aria-hidden className="invisible pointer-events-none">
        {subscription?.canUseHeadBanner && vendor?.headBanner && (
          <SubHeader fontClass={fontClass} vendor={vendor} />
        )}
        <div className="border-b border-border">
          <div className="lg:hidden py-2"><div className="h-10" /></div>
          <div className="hidden lg:block h-[76px]" />
          <div className="hidden lg:block h-12 border-t border-border" />
        </div>
        {vendor?.isDanVerified === false && <div className="h-9 border-t border-amber-200" />}
      </div>
    </>
  )
}


export function SkeletonCategoryList() {
  return (
    <div className="grid grid-cols-1 gap-4">
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
      <Skeleton className="h-full" />
    </div>
  )
}

